import React, { useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import { CloseRounded } from "@mui/icons-material";
import { set, useForm } from "react-hook-form";
import colors from "app/styles/colors";
import { ErrorToaster, SuccessToaster } from "../Toaster/Toaster";
import { InputField } from "../UI/TextField";
import { PrimaryButton } from "../Buttons/Button";
import CarService from "app/apis/Goeasy/Car/CarService";
import { SelectField } from "../UI/SelectField";

function AddNewCar({ open, handleClose, toggle, handleSubmitFunc }) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm();

  const [vehicleImage, setVehicleImage] = useState(null);
  const [btnLoader, setBtnLoader] = useState(false);

  const submitVehicleForm = async (formData) => {
    try {
      setBtnLoader(true);
      await CarService.createCarBooking(formData);
      SuccessToaster("Car saved successfully");
      resetFormState();
      handleSubmitFunc();
    } catch (error) {
      ErrorToaster(error);
    } finally {
      setBtnLoader(false);
    }
  };

  const prepareVehicleFormData = (data) => {
    const formData = new FormData();

    Object.entries(data).forEach(([key, value]) => {
      formData.append(key, value);
    });

    if (vehicleImage) {
      formData.append("image", vehicleImage, vehicleImage.name);
    } else {
      ErrorToaster("Vehicle Image is required.");
      return;
    }

    submitVehicleForm(formData);
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) setVehicleImage(file);
  };

  const resetFormState = () => {
    setVehicleImage(null);
    reset();
  };

  return (
    <Dialog
      open={open}
      fullWidth
      onClose={() => {
        handleClose();
        resetFormState();
      }}
      sx={{ "& .MuiDialog-paper": { borderRadius: 3 } }}
    >
      {/* ================= Title ================= */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontWeight: 600,
        }}
      >
        {toggle ? "Edit Car" : "Add New Car"}
        <IconButton
          onClick={() => {
            handleClose();
            resetFormState();
          }}
        >
          <CloseRounded />
        </IconButton>
      </DialogTitle>

      <Divider />

      {/* ================= Content ================= */}
      <DialogContent>
        <Box component="form" onSubmit={handleSubmit(prepareVehicleFormData)}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <InputField
                baseStyle={{ my: 0 }}
                label="Brand"
                error={errors?.brand?.message}
                register={register("brand", {
                  required: "Brand is required",
                })}
              />
            </Grid>

            <Grid item xs={6}>
              <InputField
                baseStyle={{ my: 0 }}
                label="Model"
                error={errors?.model?.message}
                register={register("model", {
                  required: "Model is required",
                })}
              />
            </Grid>
            <Grid item xs={6}>
              <InputField
                baseStyle={{ my: 0 }}
                label="Vehicle Number"
                error={errors?.vehicle_no?.message}
                register={register("vehicle_no", {
                  required: "Vehicle number is required",
                })}
              />
            </Grid>

            <Grid item xs={6}>
              <SelectField
                label="Type"
                register={register("type", {
                  required: "Type is required",
                })}
                error={errors?.type?.message}
                options={[
                  { label: "Sedan", value: "Sedan" },
                  { label: "SUV", value: "SUV" },
                  { label: "Hatchback", value: "Hatchback" },
                  { label: "Van", value: "Van" },
                ]}
              />
            </Grid>

            <Grid item xs={6}>
              <InputField
                baseStyle={{ my: 0 }}
                type={"number"}
                label="Year"
                error={errors?.year?.message}
                register={register("year", {
                  required: "Year is required",
                })}
              />
            </Grid>

            <Grid item xs={6}>
              <InputField
                baseStyle={{ my: 0 }}
                label="Seating Capacity"
                error={errors?.seating_capacity?.message}
                register={register("seating_capacity", {
                  required: "Seating capacity is required",
                })}
              />
            </Grid>
            <Grid item xs={6}>
              <InputField
                baseStyle={{ my: 0 }}
                label="Transmission"
                error={errors?.transmission?.message}
                register={register("transmission", {
                  required: "Transmission is required",
                })}
              />
            </Grid>
            <Grid item xs={6}>
              <InputField
                baseStyle={{ my: 0 }}
                label="Fuel"
                error={errors?.fuel?.message}
                register={register("fuel", {
                  required: "Fuel is required",
                })}
              />
            </Grid>

            <Grid item xs={6}>
              <InputField
                baseStyle={{ my: 0 }}
                type={"number"}
                label="Per Day Rate"
                error={errors?.per_day_rate?.message}
                register={register("per_day_rate", {
                  required: "Per day rate is required",
                  min: {
                    value: 0,
                    message: "Price cannot be less than 0",
                  },
                })}
              />
            </Grid>

            {/* ================= Image Upload ================= */}
            <Grid item xs={12}>
              <Typography>Car Image</Typography>

              <FormControl fullWidth>
                <Box
                  component="label"
                  htmlFor="vehicle-image"
                  sx={{
                    border: `2px dashed ${colors.lightGray}`,
                    borderRadius: 2,
                    height: 200,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    overflow: "hidden",
                    p: 1,
                    mb: 3,
                    "&:hover": {
                      borderColor: colors.primary,
                    },
                  }}
                >
                  {vehicleImage ? (
                    <img
                      src={URL.createObjectURL(vehicleImage)}
                      alt="Vehicle"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                      }}
                    />
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      Click to upload car image
                    </Typography>
                  )}

                  <input
                    id="vehicle-image"
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                </Box>
              </FormControl>
            </Grid>
          </Grid>

          {/* ================= Submit ================= */}
          <PrimaryButton
            loading={btnLoader}
            type="submit"
            title="Save Car"
            fullWidth
            myStyle={{ mt: 2, borderRadius: 2 }}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default AddNewCar;
